import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { fetchAuthSession, signInWithRedirect, signOut as amplifySignOut, getCurrentUser } from 'aws-amplify/auth'
import posthog from 'posthog-js'
import { logAuthState } from '../utils/authDebug'

const AuthContext = createContext(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  // Start in the loading state so ProtectedRoute shows a spinner instead of redirecting
  // to /login before the first session check has run.
  const [loading, setLoading] = useState(true)
  const checkInFlight = useRef(false)

  useEffect(() => {
    // On a Cognito OAuth return (?code&state) fetchAuthSession waits for Amplify's in-flight
    // token exchange, so no extra delay is needed here.
    checkAuth()
  }, []) // Only run once on mount

  async function checkAuth() {
    // Prevent multiple simultaneous checks
    if (checkInFlight.current) {
      return
    }
    checkInFlight.current = true

    setLoading(true)
    
    try {
      const authSession = await fetchAuthSession({ forceRefresh: false })
      
      if (authSession.tokens) {
        try {
          const currentUser = await getCurrentUser()
          // Extract user attributes from ID token payload
          const userAttributes = authSession.tokens?.idToken?.payload || {}
          
          // Build user object with name information
          const userWithAttributes = {
            ...currentUser,
            // Extract name from token attributes (Google OAuth provides 'name', 'given_name', 'family_name')
            name: userAttributes.name || userAttributes.given_name || userAttributes['custom:name'] || null,
            email: userAttributes.email || null,
            givenName: userAttributes.given_name || null,
            familyName: userAttributes.family_name || null,
            // Keep username for fallback
            username: currentUser.userId || userAttributes.email?.split('@')[0] || 'User',
          }
          
          // Only log once when user is first authenticated
          if (!user) {
            logAuthState(authSession, currentUser)
          }
          // Store both user and session for token access
          setUser(userWithAttributes)
          setSession(authSession)
        } catch (userError) {
          // Only log errors once
          if (!user) {
            console.error('Error getting current user:', userError)
            logAuthState(authSession, null, userError)
          }
          // Even if getCurrentUser fails, we have tokens, so extract from token
          const userAttributes = authSession.tokens?.idToken?.payload || {}
          setUser({ 
            username: userAttributes.email?.split('@')[0] || 'user',
            name: userAttributes.name || userAttributes.given_name || null,
            email: userAttributes.email || null,
            signedIn: true 
          })
          setSession(authSession)
        }
      } else {
        // Only log when transitioning from authenticated to unauthenticated
        if (user) {
          console.log('No tokens found, user not authenticated')
          logAuthState(authSession, null)
        }
        setUser(null)
        setSession(null)
      }
    } catch (error) {
      // Only log errors once
      if (!user) {
        console.error('Error checking auth:', error)
        logAuthState(null, null, error)
      }
      setUser(null)
      setSession(null)
    } finally {
      checkInFlight.current = false
      setLoading(false)
    }
  }

  async function signIn() {
    try {
      // `prompt: 'SELECT_ACCOUNT'` forces Google's account chooser so users can
      // pick a different account instead of silently reusing the current Google
      // session. Amplify appends `prompt=select_account` to the Cognito
      // /oauth2/authorize URL (mirrors the MCP OAuth flow) and skips its
      // "already authenticated" guard, allowing an account switch mid-session.
      await signInWithRedirect({ provider: 'Google', options: { prompt: 'SELECT_ACCOUNT' } })
    } catch (error) {
      console.error('Error signing in:', error)
      throw error
    }
  }

  async function signOut() {
    try {
      posthog.reset()
      await amplifySignOut()
      setUser(null)
      window.location.href = '/login'
    } catch (error) {
      console.error('Error signing out:', error)
      throw error
    }
  }

  const value = {
    user,
    session,
    loading,
    signIn,
    signOut,
    checkAuth,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
